import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';

import usePageTitle from '../../hooks/UsePageTitle';
const RiskManagement = () => {
  usePageTitle('Risk Management Services');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">Risk Management</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img
            src='/images/Risk Management.jpg'
            alt="Risk Management Services"
            className="w-full border-round shadow-2 mb-4"
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6 h-full">
            <p className="text-lg line-height-3">
              In today's rapidly evolving business ecosystem, risk is no longer a one-dimensional threat —
              it's multifaceted, interconnected, and often invisible until it strikes.
            </p>
            <p className="text-lg line-height-3 mt-3">
              At SnP Finvisors, we believe risk management isn't just about minimizing losses; it's about
              enhancing resilience, safeguarding stakeholder value, and enabling growth.
            </p>
            <p className="text-lg line-height-3 mt-3 font-semibold">
              Our Risk Management services are built around deep diagnostics, robust frameworks, and real-world
              controls that empower organizations to anticipate, respond, and thrive in uncertainty.
            </p>
        </div>

        {/* Why Risk Management is Crucial Section */}
        <div className="col-12 mt-5">
          <Card>
            <h2>Why Risk Management is Crucial</h2>
            <Divider />
            <p className="text-lg line-height-3">
              Every business, whether a startup or a conglomerate, faces internal and external threats —
              be it operational lapses, fraud, compliance breaches, inventory leakages, or control failures.
              Without a proactive risk management strategy, organizations may suffer:
            </p>
            <ul className="list-none p-0 m-0 mt-3">
              <li className="mb-2 flex align-items-start">
                <i className="pi pi-circle-fill text-primary mr-2 mt-1" style={{ fontSize: '0.5rem' }}></i>
                <span>Financial losses and misappropriation</span>
              </li>
              <li className="mb-2 flex align-items-start">
                <i className="pi pi-circle-fill text-primary mr-2 mt-1" style={{ fontSize: '0.5rem' }}></i>
                <span>Regulatory penalties and reputation damage</span>
              </li>
              <li className="mb-2 flex align-items-start">
                <i className="pi pi-circle-fill text-primary mr-2 mt-1" style={{ fontSize: '0.5rem' }}></i>
                <span>Stakeholder distrust</span>
              </li>
              <li className="flex align-items-start">
                <i className="pi pi-circle-fill text-primary mr-2 mt-1" style={{ fontSize: '0.5rem' }}></i>
                <span>Business discontinuity</span>
              </li>
            </ul>
            <p className="text-lg line-height-3 mt-3 font-semibold">
              We help identify, quantify, and mitigate these risks through tailored solutions grounded in
              global best practices and local realities.
            </p>
          </Card>
        </div>

        {/* Services Section */}
        <div className="col-12 mt-5">
          <h2>Our Risk Management Services</h2>
          <Divider />

          <div className="grid">
            {/* Service 1 */}
            <div className="col-12">
              <Card title="1. Internal Audit" className="mb-4">
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <p className="text-lg">
                      Our internal audits go beyond ticking boxes — they deliver insights, identify inefficiencies,
                      and recommend risk-mitigating actions.
                    </p>
                    <p className="text-lg font-semibold mt-3">We offer:</p>
                    <ul className="list-none p-0 m-0">
                      <li className="mb-2 flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Risk-based internal audit planning</span>
                      </li>
                      <li className="mb-2 flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Testing of controls and processes</span>
                      </li>
                      <li className="mb-2 flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Compliance checks (statutory, contractual, internal)</span>
                      </li>
                      <li className="mb-2 flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Fraud risk assessments</span>
                      </li>
                      <li className="flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Audit dashboards and actionable reporting</span>
                      </li>
                    </ul>
                  </div>
                  <div className="col-12 md:col-6">
                    <img
                      src='/images/Internal Audit1.png'
                      alt="Internal Audit"
                      className="w-full border-round"
                      style={{ maxHeight: '250px', objectFit: 'cover' }}
                    />
                    <p className="text-lg mt-3">
                      Whether outsourced or co-sourced, our internal audits strengthen your governance and drive value.
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Service 2 */}
            <div className="col-12 flex flex-wrap">
              <Card title="2. Design & Implementation of SOPs" className="mb-4 h-full">
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <p className="text-lg">
                      Standard Operating Procedures (SOPs) are the backbone of consistency, accountability, and compliance.
                    </p>
                    <p className="text-lg font-semibold mt-3">We provide:</p>
                    <ul className="list-none p-0 m-0">
                      <li className="mb-2 flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Map current processes and identify inefficiencies</span>
                      </li>
                      <li className="mb-2 flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Develop SOPs for key functions (finance, HR, procurement, logistics, etc.)</span>
                      </li>
                      <li className="mb-2 flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Ensure alignment with internal controls and audit trails</span>
                      </li>
                      <li className="flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Conduct staff training and periodic reviews</span>
                      </li>
                    </ul>
                    <p className="text-lg mt-3 font-semibold">
                      Clear, well-implemented SOPs help reduce errors, fraud risks, and operational dependency on individuals.
                    </p>
                  </div>
                  <div className="col-12 md:col-6">
                    <img
                      src='/images/Internal Audit3.png'
                      alt="Internal Audit"
                      className="w-full border-round"
                      style={{ maxHeight: '250px', objectFit: 'cover' }}
                    />
                  </div>
                </div>
              </Card>
            </div>

            {/* Service 3 */}
            <div className="col-12">
              <Card title="3. Inventory Management & Stock Verification" className="mb-4 h-full">
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <p className="text-lg">
                      Inventory shrinkage, mismatch, or poor tracking can lead to significant cost overruns and disputes.
                    </p>
                    <p className="text-lg font-semibold mt-3">Our approach includes:</p>
                    <ul className="list-none p-0 m-0">
                      <li className="mb-2 flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Physical verification of stock across multiple locations</span>
                      </li>
                      <li className="mb-2 flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Reconciliation with books of accounts and ERP data</span>
                      </li>
                      <li className="mb-2 flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Stock ageing and slow-moving inventory analysis</span>
                      </li>
                      <li className="flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Review of stock movement controls, pilferage risks, and vendor issues</span>
                      </li>
                    </ul>
                    <p className="text-lg mt-3">
                      Our team ensures transparency and integrity of inventory reporting, vital for manufacturing,
                      retail, and trading businesses.
                    </p>
                    </div>
                    <div className="col-12 md:col-6">
                      <img
                        src='/images/taxc.jpg'
                        alt="Internal Audit"
                        className="w-full border-round"
                        style={{ maxHeight: '250px', objectFit: 'cover' }}
                      />
                    </div>
                  </div>
              </Card>
            </div>
          </div>
        </div>

        {/* Closing Section */}
        <div className="col-12 mt-5">
          <Card className="surface-100">
            <h2 className="mt-0">Turn Uncertainty into Confidence</h2>
            <Divider />
            <p className="text-xl line-height-3">
              SnP Finvisors' Risk Management services turn uncertainty into confidence by building control
              frameworks that are strong, scalable, and sustainable.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RiskManagement;